import { useQuery } from '@tanstack/react-query';
import { axiosGetV3 } from '../../api/axios-handler';
import { getSessionSeed } from '../../utils/sessionSeed';
import { Content, Resource, People } from './useExperiences';
import { fetchVimeoMetadataBatch, addVimeoMetadataToExperiences } from './useBrowseExperiences';

export interface FavoritesContentResponse {
  count: {
    listener_audio: number;
    client_resources: number;
    global_resources: number;
    listener_roles: number;
  };
  data: {
    listener_audio: Content[];
    client_resources: Resource[];
    global_resources: Resource[];
    listener_roles: People[];
  };
}

const addVimeoMetadataToListenerAudio = async (listenerAudio: Content[]): Promise<Content[]> => {
  const videoItems = listenerAudio.filter((item) => item.video_id && !item.vimeo_thumbnail_url);

  if (videoItems.length === 0) {
    return listenerAudio;
  }

  const videoIds = Array.from(
    new Set(videoItems.map((item) => item.video_id).filter((id): id is string => Boolean(id))),
  );
  const metadataMap = await fetchVimeoMetadataBatch(videoIds);

  return listenerAudio.map((item) => addVimeoMetadataToExperiences(item, metadataMap));
};

const getFavoritesContent = async (): Promise<FavoritesContentResponse> => {
  const response = await axiosGetV3('/favorites/content', { seed: getSessionSeed() });
  const data: FavoritesContentResponse = response.data;

  // Add Vimeo metadata (thumbnails and durations) for video content
  if (data.data?.listener_audio?.length) {
    data.data.listener_audio = await addVimeoMetadataToListenerAudio(data.data.listener_audio);
  }

  return data;
};

export const useFavorites = () => {
  return useQuery<FavoritesContentResponse>(['favoritesContent'], () => getFavoritesContent(), {
    refetchOnWindowFocus: false,
  });
};
